/**
 * What a lesson must contain before it can be taught, and how a draft is cleaned into what would be learned.
 * Pure functions. The engineer gives the category (shown, not chosen), the symptoms, the troubleshooting steps taken,
 * the root cause, and the fix, mitigation or workaround applied. Arta writes the pattern, its questions and its checks from them later.
 */

// An item shorter than this has nothing to verify: it is left out and listed.
export const MIN_ITEM_CHARS = 2;
export const MIN_ROOT_CAUSE_CHARS = 5;
export const MAX_LIST_ITEMS = 20;
export const DROP_REASON_SHORT = "too short to verify";

export const SECTION = Object.freeze({ SYMPTOM: "symptoms", STEP: "steps" });
// The requirement keys, also used as the hint keys the screens show.
export const HINT = Object.freeze({ CATEGORY: "category", SYMPTOMS: "symptoms", STEPS: "steps", ROOT: "root", FIX: "fix" });

// What was done about the root cause: the server accepts the same three kinds (models.PATTERN_FIX_KINDS).
export const FIX_KIND = Object.freeze({ FIX: "fix", MITIGATION: "mitigation", WORKAROUND: "workaround" });
export const FIX_KINDS = Object.freeze([FIX_KIND.FIX, FIX_KIND.MITIGATION, FIX_KIND.WORKAROUND]);
export const MIN_FIX_CHARS = 5;
export const MAX_FIX_CHARS = 2000;

export const splitLines = (text) => String(text || "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

/** A list always shows at least one row to type in. */
export const withBlankRow = (items) => (items && items.length ? [...items] : [""]);

const longEnough = (text) => String(text || "").trim().length >= MIN_ITEM_CHARS;

/**
 * The draft as it would be learned: items with nothing to verify removed, and what was removed listed.
 * Returns {draft: {symptoms, steps, rootCause}, dropped: [{section, text, reason}]}.
 */
export function cleanDraft(draft) {
  const dropped = [];
  const clean = (section, items) => (items || []).map((t) => String(t || "").trim()).filter((t) => {
    if (longEnough(t)) return true;
    if (t) dropped.push({ section, text: t, reason: DROP_REASON_SHORT });
    return false;
  });
  return {
    draft: {
      symptoms: clean(SECTION.SYMPTOM, draft.symptoms),
      steps: clean(SECTION.STEP, draft.steps),
      rootCause: String(draft.rootCause || "").trim(),
      fixKind: FIX_KINDS.includes(draft.fixKind) ? draft.fixKind : "",
      fixText: String(draft.fixText || "").trim(),
    },
    dropped,
  };
}

/** Everything the lesson still needs, in order, as [{key, ok}] from a cleaned draft and the category. */
export function requirementsOf(category, cleaned) {
  return [
    { key: HINT.CATEGORY, ok: !!(category && category.key) },
    { key: HINT.SYMPTOMS, ok: cleaned.symptoms.length > 0 },
    { key: HINT.STEPS, ok: cleaned.steps.length > 0 },
    { key: HINT.ROOT, ok: cleaned.rootCause.length >= MIN_ROOT_CAUSE_CHARS },
    { key: HINT.FIX, ok: !!cleaned.fixKind && cleaned.fixText.length >= MIN_FIX_CHARS },
  ];
}

/** The first requirement not met, or null. */
export const firstUnmet = (category, cleaned) => requirementsOf(category, cleaned).find((r) => !r.ok) || null;
